import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Flame, Sparkles } from "lucide-react";
import PhoneMockup from "./PhoneMockup";
import RadioScreen from "./RadioScreen";
import ChatScreen from "./ChatScreen";
import DashboardScreen from "./DashboardScreen";

/**
 * CinematicHero — bold, punchy, tilted-phone-stack hero for the MeloCraft
 * landing page. Inspired by Dimensions.fm but styled with MeloCraft's
 * ember/black aesthetic. Uses framer-motion for staggered word reveal,
 * parallax phones, and a subtle scroll-linked headline drift.
 */

const HEADLINE_TOP = ["Craft", "your", "sound."];
const HEADLINE_BOTTOM = ["Sell", "it", "direct."];

const wordVariants = {
  hidden: { y: "110%", opacity: 0 },
  show: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.15 + i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function CinematicHero() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, -80]);
  const phonesY = useTransform(scrollY, [0, 500], [0, 60]);

  // ambient "sparks" (tiny drifting embers)
  const sparks = Array.from({ length: 14 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 6,
    dur: 6 + Math.random() * 6,
    size: Math.random() * 3 + 2,
  }));

  return (
    <section
      data-testid="cinematic-hero"
      className="relative overflow-hidden border-b border-[#2A2A2A]"
      style={{ minHeight: "min(92vh, 900px)" }}
    >
      {/* Background layers */}
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 40% at 15% 20%, rgba(255,69,0,0.22), transparent 70%), radial-gradient(50% 50% at 85% 80%, rgba(255,69,0,0.15), transparent 70%)",
        }}
      />
      {/* Grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
        }}
      />
      {/* Drifting sparks */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {sparks.map((s) => (
          <motion.span
            key={s.id}
            className="absolute rounded-full bg-[#FF4500] blur-[1px]"
            style={{ left: `${s.left}%`, bottom: "-10px", width: s.size, height: s.size }}
            animate={{ y: [-40, -700], opacity: [0, 1, 0] }}
            transition={{ duration: s.dur, delay: s.delay, repeat: Infinity, ease: "easeOut" }}
          />
        ))}
      </div>

      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-10 lg:gap-6 items-center">
          {/* Left — copy */}
          <motion.div style={{ y: heroY }} className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-[#FF4500]/40 bg-[#FF4500]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF4500] mb-6"
            >
              <Flame className="h-3.5 w-3.5" />
              <span>The studio is open · Est. 2026</span>
            </motion.div>

            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl xl:text-[92px] leading-[0.9] tracking-tighter">
              <span className="block overflow-hidden">
                <span className="flex flex-wrap gap-x-3">
                  {HEADLINE_TOP.map((w, i) => (
                    <motion.span
                      key={i}
                      className="inline-block"
                      custom={i}
                      variants={wordVariants}
                      initial="hidden"
                      animate="show"
                    >
                      {w}
                    </motion.span>
                  ))}
                </span>
              </span>
              <span className="block overflow-hidden text-[#FF4500]">
                <span className="flex flex-wrap gap-x-3">
                  {HEADLINE_BOTTOM.map((w, i) => (
                    <motion.span
                      key={i}
                      className="inline-block"
                      custom={i + HEADLINE_TOP.length}
                      variants={wordVariants}
                      initial="hidden"
                      animate="show"
                    >
                      {w}
                    </motion.span>
                  ))}
                </span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="font-mono text-sm md:text-base text-gray-300 mt-7 max-w-xl leading-relaxed"
            >
              An artist-first marketplace. Songs for <span className="text-white">$0.99</span>.
              Albums for <span className="text-white">$15</span>. Free radio for everyone.
              <br className="hidden sm:block" />
              <span className="text-[#FF4500]">$2/mo</span> unlocks unlimited selling — you keep every cent of the sale.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.6 }}
              className="flex flex-wrap items-center gap-3 mt-8"
            >
              <Link to="/register" data-testid="cinema-become-artist">
                <button className="group bg-[#FF4500] text-black h-12 px-6 font-mono text-xs uppercase tracking-widest hover:bg-[#E53E00] transition-colors flex items-center gap-2 shadow-[0_10px_40px_-10px_rgba(255,69,0,0.9)]">
                  Start selling for $2/mo
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/browse" data-testid="cinema-play-radio">
                <button className="group border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] h-12 px-6 font-mono text-xs uppercase tracking-widest transition-colors flex items-center gap-2 bg-black/40 backdrop-blur">
                  <Play className="h-4 w-4" fill="currentColor" />
                  Play the radio
                </button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.6 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-widest text-gray-500"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-3 w-3 text-[#FF4500]" /> AI mastering — free
              </div>
              <div>· No royalty cut</div>
              <div>· Stripe payouts</div>
              <div>· Own merch storefront</div>
            </motion.div>
          </motion.div>

          {/* Right — phone stack */}
          <motion.div
            style={{ y: phonesY, transformStyle: "preserve-3d" }}
            className="relative flex items-center justify-center lg:justify-end min-h-[520px] lg:min-h-[640px]"
          >
            {/* Back phone — Dashboard */}
            <div className="absolute right-0 top-2 hidden md:block">
              <PhoneMockup tilt={14} rotateX={6} delay={0.6} floatRange={16} testId="hero-phone-dashboard">
                <DashboardScreen />
              </PhoneMockup>
            </div>
            {/* Middle phone — Chat, offset */}
            <div className="absolute left-2 md:left-10 top-24 md:top-32 hidden sm:block">
              <PhoneMockup tilt={-16} rotateX={10} delay={0.4} floatRange={20} testId="hero-phone-chat">
                <ChatScreen />
              </PhoneMockup>
            </div>
            {/* Front phone — Radio (most prominent) */}
            <div className="relative z-10 md:mr-32 md:mt-16">
              <PhoneMockup tilt={-4} rotateX={4} delay={0.2} floatRange={12} testId="hero-phone-radio">
                <RadioScreen />
              </PhoneMockup>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom cinematic fade */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-[#0A0A0A] pointer-events-none" />
    </section>
  );
}
